const VARIABLE_PREFIX = "--keyreply-";

/**
 * Update theme variables using CSS custom properties (CSP compliant)
 * Instead of injecting style elements, we set CSS custom properties on the root element
 * using setProperty() which is allowed by CSP
 */
export function updateThemeVariables(settings: any) {
  if (!settings) return;

  // Get the root element to set CSS custom properties
  const root = document.documentElement;

  // Set color variables using setProperty (CSP compliant)
  if (settings.agentTextColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}agent-text-color`, settings.agentTextColor);
  }

  if (settings.agentBubbleColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}agent-bubble-color`, settings.agentBubbleColor);
  }

  if (settings.userTextColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}user-text-color`, settings.userTextColor);
  }

  if (settings.userBubbleColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}user-bubble-color`, settings.userBubbleColor);
  }

  if (settings.headerBackgroundColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}header-bg-color`, settings.headerBackgroundColor);
  }

  if (settings.headerFontColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}header-font-color`, settings.headerFontColor);
  }

  // Set font family variable
  if (settings.font) {
    root.style.setProperty(`${VARIABLE_PREFIX}font-family`, settings.font);
  }

  // Set background variable if exists
  if (settings.background) {
    root.style.setProperty(`${VARIABLE_PREFIX}background-image`, `url('${settings.background}')`);
  } else {
    root.style.setProperty(`${VARIABLE_PREFIX}background-image`, "none");
  }

  // Set UI Beta theme colors (used in Carousel, Compact, etc.)
  if (settings.userMainBackgroundColor) {
    root.style.setProperty(
      `${VARIABLE_PREFIX}user-main-bg-color`,
      settings.userMainBackgroundColor
    );
  }

  if (settings.userMainTitleColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}user-main-title-color`, settings.userMainTitleColor);
  }

  if (settings.widgetIconColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}widget-icon-bg-color`, settings.widgetIconColor);
  }

  if (settings.widgetIconTextColor) {
    root.style.setProperty(
      `${VARIABLE_PREFIX}widget-icon-text-color`,
      settings.widgetIconTextColor
    );
  }

  // Additional color settings
  if (settings.color) {
    root.style.setProperty(`${VARIABLE_PREFIX}primary-color`, settings.color);
    // Also set link color to primary color by default
    root.style.setProperty(`${VARIABLE_PREFIX}link-color`, settings.color);
  }

  if (settings.headerColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}header-color`, settings.headerColor);
  }

  if (settings.headerBorderColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}header-border-color`, settings.headerBorderColor);
  }

  if (settings.buttonOutlineColor) {
    root.style.setProperty(`${VARIABLE_PREFIX}button-outline-color`, settings.buttonOutlineColor);
  }

  if (settings.headerModalIconColor) {
    root.style.setProperty(
      `${VARIABLE_PREFIX}header-modal-icon-color`,
      settings.headerModalIconColor
    );
  }

  if (settings.trendingTopicBanner) {
    root.style.setProperty(`${VARIABLE_PREFIX}trending-topic-banner`, settings.trendingTopicBanner);
  }

  if (settings.trendingTopicTitle) {
    root.style.setProperty(`${VARIABLE_PREFIX}trending-topic-title`, settings.trendingTopicTitle);
  }

  // Video VA subtitle font size (capped at 24px)
  if (settings.videoVAConfig?.subtitleFontSize) {
    const fontSize = Math.min(settings.videoVAConfig.subtitleFontSize, 24);
    root.style.setProperty(`${VARIABLE_PREFIX}subtitle-font-size`, `${fontSize}px`);
  }

  // Slider main color (use primary color as default)
  if (settings.color) {
    root.style.setProperty(`${VARIABLE_PREFIX}slider-main-color`, settings.color);
  }

  // Launcher-specific variables
  if (settings.launcherBorderRadius) {
    root.style.setProperty(
      `${VARIABLE_PREFIX}launcher-border-radius`,
      settings.launcherBorderRadius
    );
  } else {
    root.style.setProperty(`${VARIABLE_PREFIX}launcher-border-radius`, "50%");
  }

  if (settings.botIcon) {
    root.style.setProperty(
      `${VARIABLE_PREFIX}launcher-background-image`,
      `url(${settings.botIcon})`
    );
  } else if (settings.color) {
    // Generate default SVG with color
    const color = encodeURIComponent(settings.color);
    const svg = `url("data:image/svg+xml;charset=utf8,%3Csvg width='26' height='26' viewBox='0 0 26 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cellipse fill='${color}' cx='13' cy='13' rx='12' ry='12'/%3E%3Cpath d='M6.798 15.503l1.453-.92c.617.215 1.29.334 2 .334 2.898 0 5.247-1.996 5.247-4.46 0-2.46-2.35-4.457-5.248-4.457C7.35 6 5 7.996 5 10.458c0 1.446.81 2.73 2.065 3.545l-.503 1.273c-.038.03-.062.076-.062.127 0 .09.074.162.166.162.054 0 .1-.024.132-.062z' stroke='${color}' stroke-width='.2' fill='%23FFF'/%3E%3Cpath d='M20.297 18.97l.04-.065-.578-1.155c1.066-.814 1.737-1.993 1.737-3.305 0-2.455-2.35-4.445-5.248-4.445-2.9 0-5.25 1.99-5.25 4.445s2.35 4.445 5.25 4.445c.838 0 1.63-.167 2.334-.463l1.39.756c.035.05.095.085.163.085.107 0 .194-.085.194-.19 0-.04-.012-.076-.033-.107z' stroke='${color}' stroke-width='.2' fill='%23FFF'/%3E%3C/g%3E%3C/svg%3E")`;
    root.style.setProperty(`${VARIABLE_PREFIX}launcher-background-image`, svg);
  }
}

/**
 * Clear all theme variables from the root element
 */
export function clearThemeVariables() {
  const root = document.documentElement;
  const style = root.style;

  // Remove all keyreply CSS variables
  for (let i = style.length - 1; i >= 0; i--) {
    const prop = style[i];
    if (prop.startsWith(VARIABLE_PREFIX)) {
      root.style.removeProperty(prop);
    }
  }
}

export default {
  updateThemeVariables,
  clearThemeVariables,
};
